import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { simulateFeeOnChain, getTokenSimulationMeta, SimulationResult, computeCotiFee, computeErc20Fee, fetchTokenUsdPrice, simulatePodPortalFee } from '@/vendor/coti-wallet-plugin';
import { getChainConfig, getRpcUrlForChain } from '@/chains';
import { POD_PORTAL_FACTORY_ABI, POD_PORTAL_ADMIN_ABI } from '@/contracts/pod';
import { BRIDGE_ABI, BRIDGE_ERC20_ABI, ERC20_ABI } from '@/contracts/config';
import { formatAdminError } from '@/lib/formatAdminError';
import { formatPlainDecimal } from '@/lib/utils';

export type AdminActionType = 'setDepositFee' | 'setWithdrawFee' | 'setLimits' | 'rescueCoti' | 'rescueNative' | 'pause' | 'unpause' | 'enableDeposits' | 'disableDeposits';

export interface CurrentFees {
  depositFixedFee: string;
  depositPercentageBps: string;
  depositMaxFee: string;
  withdrawFixedFee: string;
  withdrawPercentageBps: string;
  withdrawMaxFee: string;
  minDepositAmount: string;
  maxDepositAmount: string;
  minWithdrawAmount: string;
  maxWithdrawAmount: string;
}

interface AdminActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionType: AdminActionType;
  bridgeName: string;
  bridgeAddress: string;
  tokenSymbol?: string;
  /** Underlying ERC-20 for token rescue balance (PoD wrapped natives / ERC-20 portals). */
  tokenAddress?: string;
  tokenDecimals?: number;
  currentFees?: CurrentFees;
  chainId?: number;
  /** PoD privacy portal (Sepolia/Fuji): fees are native-coin denominated, simulation is local. */
  isPod?: boolean;
  /** Symbol the fee values are denominated in (ETH/AVAX on PoD chains; COTI otherwise). */
  feeSymbol?: string;
  isNativeBridge?: boolean;
  onExecute: (params: any) => Promise<void>;
}

// ─── Simulate Fee Panel ──────────────────────────────────────────────────────

interface SimulatePanelProps {
  fixedFee: string;
  percentageBps: string;
  maxFee: string;
  actionType: 'setDepositFee' | 'setWithdrawFee';
  tokenSymbol: string;
  bridgeAddress: string;
  chainId: number;
  isPod: boolean;
  feeSymbol: string;
}

const SimulatePanel = ({ fixedFee, percentageBps, maxFee, actionType, tokenSymbol, bridgeAddress, chainId, isPod, feeSymbol }: SimulatePanelProps) => {
  const [customAmount, setCustomAmount] = useState('');
  const [customResult, setCustomResult] = useState<SimulationResult | null>(null);
  const [scenarioResults, setScenarioResults] = useState<(SimulationResult & { label: string; amount: string })[]>([]);
  const [isSimulating, setIsSimulating] = useState(false);

  const { oracleSymbol, decimals } = getTokenSimulationMeta(tokenSymbol || 'COTI');
  const isNative = tokenSymbol === 'COTI' || !tokenSymbol;

  const getProvider = useCallback(() => {
    return new ethers.JsonRpcProvider(getRpcUrlForChain(chainId));
  }, [chainId]);

  // Some deployed bridges predate the computeCotiFee/computeErc20Fee views;
  // when the on-chain call reverts, fall back to the local mirror of the
  // Solidity fee math using oracle prices from the price consumer.
  // PoD portals have no simulation views at all — always compute locally.
  const simulateFee = useCallback(async (amount: string): Promise<SimulationResult> => {
    if (isPod) {
      return simulatePodPortalFee(
        chainId, tokenSymbol, amount,
        fixedFee || '0', percentageBps || '0', maxFee || '0'
      );
    }
    const provider = getProvider();
    const onChain = await simulateFeeOnChain(
      bridgeAddress, amount, fixedFee, percentageBps, maxFee,
      oracleSymbol, decimals, provider
    );
    if (onChain.fee !== '—') return onChain;

    try {
      const cotiUsdPrice = await fetchTokenUsdPrice('COTI', provider, chainId);
      const tokenUsdPrice = isNative
        ? cotiUsdPrice
        : await fetchTokenUsdPrice(tokenSymbol, provider, chainId);
      if (cotiUsdPrice == null || tokenUsdPrice == null) return onChain;

      const fee = isNative
        ? computeCotiFee(amount, fixedFee || '0', percentageBps || '0', maxFee || '0', cotiUsdPrice)
        : computeErc20Fee(amount, decimals, fixedFee || '0', percentageBps || '0', maxFee || '0', tokenUsdPrice, cotiUsdPrice);

      const feeWei = ethers.parseEther(fee);
      const fixedFeeWei = ethers.parseEther(fixedFee || '0');
      const maxFeeWei = ethers.parseEther(maxFee || '0');
      const explanation =
        feeWei === maxFeeWei && maxFeeWei > 0n ? 'Max fee cap applied'
        : feeWei === fixedFeeWei ? 'Fixed fee floor applied'
        : 'Percentage fee applied';
      return { fee: parseFloat(fee).toFixed(4), explanation };
    } catch {
      return onChain;
    }
  }, [bridgeAddress, fixedFee, percentageBps, maxFee, oracleSymbol, decimals, tokenSymbol, isNative, isPod, chainId, getProvider]);

  const scenarios: { label: string; amount: string }[] = isNative
    ? [
        { label: 'Minimum Deposit', amount: '1' },
        { label: 'Small Deposit', amount: '10' },
        { label: 'Medium Deposit', amount: '100' },
        { label: 'Large Deposit', amount: '10000' },
      ]
    : tokenSymbol === 'WBTC'
    ? [
        { label: 'Minimum', amount: '0.0001' },
        { label: 'Small', amount: '0.001' },
        { label: 'Medium', amount: '0.01' },
        { label: 'Large', amount: '1' },
      ]
    : tokenSymbol === 'WETH' || tokenSymbol === 'ETH'
    ? [
        { label: 'Minimum', amount: '0.01' },
        { label: 'Small', amount: '0.1' },
        { label: 'Medium', amount: '1' },
        { label: 'Large', amount: '10' },
      ]
    : tokenSymbol === 'AVAX'
    ? [
        { label: 'Minimum', amount: '0.1' },
        { label: 'Small', amount: '1' },
        { label: 'Medium', amount: '10' },
        { label: 'Large', amount: '100' },
      ]
    : tokenSymbol === 'gCOTI'
    ? [
        { label: 'Minimum', amount: '1' },
        { label: 'Small', amount: '100' },
        { label: 'Medium', amount: '1000' },
        { label: 'Large', amount: '10000' },
      ]
    : tokenSymbol === 'NIGHT'
    ? [
        { label: 'Minimum', amount: '1' },
        { label: 'Small', amount: '100' },
        { label: 'Medium', amount: '1000' },
        { label: 'Large', amount: '10000' },
      ]
    : [
        { label: 'Minimum', amount: '1' },
        { label: 'Small', amount: '10' },
        { label: 'Medium', amount: '100' },
        { label: 'Large', amount: '10000' },
      ];

  // Run all scenario simulations on-chain
  const runAllSimulations = useCallback(async () => {
    if (!bridgeAddress || !fixedFee || !percentageBps || !maxFee) return;
    setIsSimulating(true);
    try {
      const results = await Promise.all(
        scenarios.map(async (s) => {
          const result = await simulateFee(s.amount);
          return { ...s, ...result };
        })
      );
      setScenarioResults(results);
    } catch (err) {
      console.error('Scenario simulation failed:', err);
    } finally {
      setIsSimulating(false);
    }
  }, [bridgeAddress, fixedFee, percentageBps, maxFee, simulateFee]);

  // Re-run scenarios when fee parameters change
  useEffect(() => {
    runAllSimulations();
  }, [runAllSimulations]);

  // Run custom simulation
  const handleSimulate = useCallback(async () => {
    if (!customAmount || parseFloat(customAmount) <= 0 || !bridgeAddress) return;
    setIsSimulating(true);
    try {
      const result = await simulateFee(customAmount);
      setCustomResult(result);
    } catch (err) {
      console.error('Custom simulation failed:', err);
      setCustomResult({ fee: '—', explanation: 'Contract call failed' });
    } finally {
      setIsSimulating(false);
    }
  }, [customAmount, bridgeAddress, simulateFee]);

  // Re-calculate custom result when fee parameters change
  useEffect(() => {
    if (customAmount && parseFloat(customAmount) > 0 && bridgeAddress) {
      simulateFee(customAmount)
        .then(setCustomResult)
        .catch(() => setCustomResult({ fee: '—', explanation: 'Contract call failed' }));
    }
  }, [fixedFee, percentageBps, maxFee]);

  const direction = actionType === 'setDepositFee' ? 'deposit' : 'withdrawal';
  const tokenLabel = tokenSymbol || 'COTI';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-primary mb-1">Simulate Fee</h3>
        <p className="text-xs text-muted-foreground">
          Quickly test different {direction} amounts to see calculated fees.
        </p>
        {isSimulating && (
          <p className="text-xs text-muted-foreground mt-1">Simulating on-chain...</p>
        )}
      </div>

      {/* Simulation Controls */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Token Amount ({tokenLabel})</Label>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={`Amount in ${tokenLabel}`}
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            className="glass flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleSimulate()}
          />
          <Button
            size="sm"
            onClick={handleSimulate}
            disabled={isSimulating}
            className="bg-primary hover:bg-primary/90 whitespace-nowrap"
          >
            {isSimulating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Simulate'}
          </Button>
        </div>
      </div>

      {/* Scenarios Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Scenario</TableHead>
              <TableHead className="text-xs text-right">Amount ({tokenLabel})</TableHead>
              <TableHead className="text-xs text-right">Fee ({feeSymbol})</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scenarioResults.length > 0 ? scenarioResults.map((r) => (
              <TableRow key={r.label}>
                <TableCell className="text-xs py-2">{r.label}</TableCell>
                <TableCell className="text-xs text-right py-2 font-mono">{parseFloat(r.amount).toLocaleString()}</TableCell>
                <TableCell className="text-xs text-right py-2">
                  <div className="font-mono">{r.fee}</div>
                  <div className="text-[10px] text-muted-foreground">({r.explanation})</div>
                </TableCell>
              </TableRow>
            )) : scenarios.map((s) => (
              <TableRow key={s.label}>
                <TableCell className="text-xs py-2">{s.label}</TableCell>
                <TableCell className="text-xs text-right py-2 font-mono">{parseFloat(s.amount).toLocaleString()}</TableCell>
                <TableCell className="text-xs text-right py-2 text-muted-foreground">—</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Custom Simulation Result */}
      {customResult && (
        <div className="border border-primary/50 rounded-md p-3 bg-primary/5">
          <div className="text-xs font-semibold text-primary mb-1">Custom Simulation Result</div>
          <div className="text-sm">
            Token Amount: <span className="font-mono font-semibold">{parseFloat(customAmount).toLocaleString()} {tokenLabel}</span>
          </div>
          <div className="text-sm">
            Calculated Fee: <span className="font-mono font-semibold">{customResult.fee} {feeSymbol}</span>
          </div>
          <div className="text-[10px] text-muted-foreground">({customResult.explanation})</div>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground italic">
        {isPod
          ? '*Simulations use the local fee formula with live PoD oracle prices.*'
          : '*Simulations call the on-chain simulateFee function using live oracle prices.*'}
      </p>
    </div>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

const AdminActionModal = ({
  isOpen,
  onClose,
  actionType,
  bridgeName,
  bridgeAddress,
  tokenSymbol = '',
  tokenAddress = '',
  tokenDecimals = 18,
  currentFees,
  chainId = 7082400,
  isPod = false,
  feeSymbol = 'COTI',
  isNativeBridge = false,
  onExecute
}: AdminActionModalProps) => {
  const [value, setValue] = useState('');
  const [fixedFee, setFixedFee] = useState('');
  const [percentageBps, setPercentageBps] = useState('');
  const [maxFee, setMaxFee] = useState('');
  const [minDeposit, setMinDeposit] = useState('');
  const [maxDeposit, setMaxDeposit] = useState('');
  const [minWithdraw, setMinWithdraw] = useState('');
  const [maxWithdraw, setMaxWithdraw] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hardcodedRecipient, setHardcodedRecipient] = useState<string | null>(null);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [availableBalance, setAvailableBalance] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const isRescueAction = actionType === 'rescueCoti' || actionType === 'rescueNative';
  const rescueAmountSymbol = actionType === 'rescueCoti' ? feeSymbol : (tokenSymbol || 'tokens');

  // Pre-populate fields with current on-chain values when the modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (currentFees) {
      const isDeposit = actionType === 'setDepositFee';
      const clean = (v: string | undefined) =>
        !v || v === 'N/A' || v === 'Error' || v === '—' ? '' : formatPlainDecimal(v);
      setFixedFee(clean(isDeposit ? currentFees.depositFixedFee : currentFees.withdrawFixedFee));
      setPercentageBps(clean(isDeposit ? currentFees.depositPercentageBps : currentFees.withdrawPercentageBps));
      setMaxFee(clean(isDeposit ? currentFees.depositMaxFee : currentFees.withdrawMaxFee));
      setMinDeposit(clean(currentFees.minDepositAmount));
      setMaxDeposit(clean(currentFees.maxDepositAmount));
      setMinWithdraw(clean(currentFees.minWithdrawAmount));
      setMaxWithdraw(clean(currentFees.maxWithdrawAmount));
    }
  }, [isOpen, actionType, currentFees]);

  // Load the contract's fixed recipient (not editable — set at deploy / on-chain config)
  useEffect(() => {
    if (!isOpen || !isRescueAction || !bridgeAddress) {
      setHardcodedRecipient(null);
      return;
    }

    let cancelled = false;
    const loadRecipient = async () => {
      setRecipientLoading(true);
      setHardcodedRecipient(null);
      try {
        const provider = new ethers.JsonRpcProvider(getRpcUrlForChain(chainId));
        if (isPod) {
          const factoryAddress = getChainConfig(chainId)?.addresses.PrivacyPortalFactory;
          if (!factoryAddress) throw new Error('PrivacyPortalFactory not configured');
          const factory = new ethers.Contract(factoryAddress, POD_PORTAL_FACTORY_ABI, provider);
          // Fee rescue → factory.feeRecipient; token/native rescue → factory.rescueRecipient
          const recipient: string =
            actionType === 'rescueCoti'
              ? await factory.feeRecipient()
              : await factory.rescueRecipient();
          if (!cancelled) setHardcodedRecipient(recipient);
          return;
        }
        const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
        const contract = new ethers.Contract(bridgeAddress, abi, provider);
        // withdrawCotiFees → feeRecipient; rescueNative/rescueERC20 → rescueRecipient
        const recipient: string =
          actionType === 'rescueCoti'
            ? await contract.feeRecipient()
            : await contract.rescueRecipient();
        if (!cancelled) setHardcodedRecipient(recipient);
      } catch (err) {
        console.error('Failed to load rescue recipient:', err);
        if (!cancelled) setHardcodedRecipient(null);
      } finally {
        if (!cancelled) setRecipientLoading(false);
      }
    };

    void loadRecipient();
    return () => { cancelled = true; };
  }, [isOpen, isRescueAction, actionType, bridgeAddress, chainId, isNativeBridge, isPod]);

  // Load available balance for rescue amount (fees or token collateral)
  useEffect(() => {
    if (!isOpen || !isRescueAction || !bridgeAddress) {
      setAvailableBalance(null);
      return;
    }

    let cancelled = false;
    const loadBalance = async () => {
      setBalanceLoading(true);
      setAvailableBalance(null);
      try {
        const provider = new ethers.JsonRpcProvider(getRpcUrlForChain(chainId));

        if (actionType === 'rescueCoti') {
          if (isPod) {
            const portal = new ethers.Contract(bridgeAddress, POD_PORTAL_ADMIN_ABI, provider);
            const fees: bigint = await portal.accumulatedPortalFees();
            if (!cancelled) setAvailableBalance(ethers.formatEther(fees));
          } else {
            const abi = isNativeBridge ? BRIDGE_ABI : BRIDGE_ERC20_ABI;
            const contract = new ethers.Contract(bridgeAddress, abi, provider);
            const fees: bigint = await contract.accumulatedCotiFees();
            if (!cancelled) setAvailableBalance(ethers.formatEther(fees));
          }
          return;
        }

        // Token / native collateral rescue
        let resolvedToken = tokenAddress;
        if (!resolvedToken && isPod && tokenSymbol) {
          const config = getChainConfig(chainId);
          const token = config?.tokens.find(t => !t.isPrivate && t.symbol === tokenSymbol);
          if (token?.addressKey && config?.addresses[token.addressKey]) {
            resolvedToken = config.addresses[token.addressKey];
          }
        }

        if (resolvedToken) {
          const erc20 = new ethers.Contract(resolvedToken, ERC20_ABI, provider);
          const bal: bigint = await erc20.balanceOf(bridgeAddress);
          if (!cancelled) setAvailableBalance(ethers.formatUnits(bal, tokenDecimals));
        } else if (isNativeBridge || (isPod && !resolvedToken)) {
          const bal = await provider.getBalance(bridgeAddress);
          if (!cancelled) setAvailableBalance(ethers.formatEther(bal));
        } else {
          if (!cancelled) setAvailableBalance(null);
        }
      } catch (err) {
        console.error('Failed to load rescue balance:', err);
        if (!cancelled) setAvailableBalance(null);
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    };

    void loadBalance();
    return () => { cancelled = true; };
  }, [
    isOpen,
    isRescueAction,
    actionType,
    bridgeAddress,
    chainId,
    isNativeBridge,
    isPod,
    tokenAddress,
    tokenDecimals,
    tokenSymbol,
  ]);

  const isFeeAction = actionType === 'setDepositFee' || actionType === 'setWithdrawFee';
  const isLimitsAction = actionType === 'setLimits';
  const needsValue = isRescueAction;
  const rescueDecimals = actionType === 'rescueCoti' ? 18 : tokenDecimals;

  const parseRescueAmount = (amountStr: string): bigint | null => {
    try {
      const trimmed = amountStr.trim();
      if (!trimmed) return null;
      return ethers.parseUnits(trimmed, rescueDecimals);
    } catch {
      return null;
    }
  };

  const availableBalanceWei =
    availableBalance != null ? parseRescueAmount(availableBalance) : null;
  const enteredAmountWei = parseRescueAmount(value);
  const hasPositiveBalance =
    availableBalanceWei != null && availableBalanceWei > 0n;
  const exceedsAvailable =
    availableBalanceWei != null &&
    enteredAmountWei != null &&
    enteredAmountWei > availableBalanceWei;
  const rescueAmountInvalid =
    isRescueAction &&
    (balanceLoading ||
      availableBalance == null ||
      enteredAmountWei == null ||
      enteredAmountWei <= 0n ||
      exceedsAvailable);

  const getTitle = () => {
    switch (actionType) {
      case 'setDepositFee': return 'Set Deposit Fee';
      case 'setWithdrawFee': return 'Set Withdraw Fee';
      case 'setLimits': return 'Set Limits';
      case 'rescueCoti': return isPod ? `Rescue ${feeSymbol} Fees` : 'Rescue COTI Fees';
      case 'rescueNative': return `Rescue ${tokenSymbol || 'Token'}`;
      case 'pause': return 'Pause Contract';
      case 'unpause': return 'Unpause Contract';
      case 'enableDeposits': return 'Enable Deposits';
      case 'disableDeposits': return 'Disable Deposits';
      default: return 'Admin Action';
    }
  };

  const getDescription = () => {
    switch (actionType) {
      case 'setDepositFee': return `Configure the dynamic deposit fee: fixed floor (${feeSymbol}), percentage (basis points), and max cap (${feeSymbol}).`;
      case 'setWithdrawFee': return `Configure the dynamic withdraw fee: fixed floor (${feeSymbol}), percentage (basis points), and max cap (${feeSymbol}).`;
      case 'setLimits': return 'Set min/max deposit and withdraw amounts. Values are in token units and will be converted automatically.';
      case 'rescueCoti': return isPod
        ? `Withdraw accumulated ${feeSymbol} portal fees (withdrawPortalFees).`
        : 'Withdraw accumulated COTI fees to the contract fee recipient.';
      case 'rescueNative': return isPod
        ? `Rescue ${tokenSymbol || 'token'} collateral (rescueERC20). The portal must be paused first.`
        : `Rescue ${tokenSymbol || 'token'} from this contract to the hardcoded rescue recipient.`;
      case 'pause': return isPod
        ? 'Pause this portal instance. Users will not be able to deposit or withdraw. Required before token rescue.'
        : 'Pause all bridge operations. Users will not be able to deposit or withdraw.';
      case 'unpause': return 'Resume bridge operations. Users will be able to deposit and withdraw again.';
      case 'enableDeposits': return 'Allow users to deposit into this bridge again. Withdrawals are unaffected.';
      case 'disableDeposits': return 'Block new deposits only. Users can still withdraw. Calls setIsDepositEnabled(false).';
      default: return '';
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      let params: any = {};

      if (
        actionType === 'pause' ||
        actionType === 'unpause' ||
        actionType === 'enableDeposits' ||
        actionType === 'disableDeposits'
      ) {
        params = {};
      } else if (isFeeAction) {
        if (!fixedFee || !percentageBps || !maxFee) {
          throw new Error('Please provide all three fee parameters');
        }
        try {
          const fixedFeeWei = ethers.parseEther(fixedFee).toString();
          const maxFeeWei = ethers.parseEther(maxFee).toString();
          const bps = parseInt(percentageBps);
          if (isNaN(bps) || bps < 0) throw new Error('Invalid basis points');
          params = { fixedFee: fixedFeeWei, percentageBps: bps.toString(), maxFee: maxFeeWei };
        } catch (err: any) {
          throw new Error(err.message || 'Invalid fee values. Please check your inputs.');
        }

      } else if (isLimitsAction) {
        if (!minDeposit || !maxDeposit || !minWithdraw || !maxWithdraw) {
          throw new Error('Please provide all four limit values');
        }
        params = { minDeposit, maxDeposit, minWithdraw, maxWithdraw };
      } else if (isRescueAction) {
        if (!value) {
          throw new Error('Please provide an amount');
        }
        const amountWei = parseRescueAmount(value);
        if (amountWei == null || amountWei <= 0n) {
          throw new Error('Amount must be greater than zero');
        }
        if (availableBalance != null) {
          const availableWei = parseRescueAmount(availableBalance);
          if (availableWei != null && amountWei > availableWei) {
            throw new Error(`Amount exceeds available balance (${availableBalance} ${rescueAmountSymbol})`);
          }
        }
        params = { amount: value };
      }

      await onExecute(params);
      setSuccess(true);
      setTimeout(() => { handleClose(); }, 2000);
    } catch (err) {
      console.error('Admin action error:', err);
      setError(formatAdminError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setValue('');
    setFixedFee('');
    setPercentageBps('');
    setMaxFee('');
    setMinDeposit('');
    setMaxDeposit('');
    setMinWithdraw('');
    setMaxWithdraw('');
    setHardcodedRecipient(null);
    setAvailableBalance(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  // Use wider dialog for fee actions to accommodate the simulation panel
  const dialogMaxWidth = isFeeAction ? 'sm:max-w-[900px]' : 'sm:max-w-[500px]';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`glass overflow-hidden ${dialogMaxWidth}`}>
        <DialogHeader>
          <DialogTitle className="text-glow">{getTitle()}</DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>

        <div className={`min-w-0 ${isFeeAction ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : ''}`}>
          {/* Left Panel: Configuration */}
          <div className="space-y-4 py-4 min-w-0">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Bridge</Label>
              <div className="text-sm text-muted-foreground">
                <div>{bridgeName}</div>
                <code className="text-xs bg-muted px-2 py-1 rounded mt-1 inline-block">
                  {bridgeAddress}
                </code>
              </div>
            </div>

            {isFeeAction && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fixedFee">Fixed Fee ({feeSymbol})</Label>
                  <Input
                    id="fixedFee"
                    type="text"
                    placeholder={isPod ? 'e.g., 0.00001' : 'e.g., 10.0'}
                    value={fixedFee}
                    onChange={(e) => setFixedFee(e.target.value)}
                    className="glass"
                    disabled={isLoading || success}
                  />
                  <p className="text-xs text-muted-foreground">Floor fee in {feeSymbol}. Converted to wei automatically.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="percentageBps">Percentage (basis points)</Label>
                  <Input
                    id="percentageBps"
                    type="text"
                    placeholder="e.g., 500 for 0.05%"
                    value={percentageBps}
                    onChange={(e) => setPercentageBps(e.target.value)}
                    className="glass"
                    disabled={isLoading || success}
                  />
                  <p className="text-xs text-muted-foreground">Relative to FEE_DIVISOR (1,000,000). 500 = 0.05%, 1000 = 0.1%</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxFee">Max Fee Cap ({feeSymbol})</Label>
                  <Input
                    id="maxFee"
                    type="text"
                    placeholder={isPod ? 'e.g., 0.1 (0 = no cap)' : 'e.g., 3000.0'}
                    value={maxFee}
                    onChange={(e) => setMaxFee(e.target.value)}
                    className="glass"
                    disabled={isLoading || success}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum fee cap in {feeSymbol}. Converted to wei automatically.{isPod ? ' Use 0 for no cap.' : ''}
                  </p>
                </div>
              </>
            )}

            {isLimitsAction && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="minDeposit">Min Deposit (token units)</Label>
                  <Input id="minDeposit" type="text" placeholder="e.g., 0.001" value={minDeposit} onChange={(e) => setMinDeposit(e.target.value)} className="glass" disabled={isLoading || success} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxDeposit">Max Deposit (token units)</Label>
                  <Input id="maxDeposit" type="text" placeholder="e.g., 1000" value={maxDeposit} onChange={(e) => setMaxDeposit(e.target.value)} className="glass" disabled={isLoading || success} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="minWithdraw">Min Withdraw (token units)</Label>
                  <Input id="minWithdraw" type="text" placeholder="e.g., 0.001" value={minWithdraw} onChange={(e) => setMinWithdraw(e.target.value)} className="glass" disabled={isLoading || success} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxWithdraw">Max Withdraw (token units)</Label>
                  <Input id="maxWithdraw" type="text" placeholder="e.g., 1000" value={maxWithdraw} onChange={(e) => setMaxWithdraw(e.target.value)} className="glass" disabled={isLoading || success} />
                </div>
              </>
            )}

            {isRescueAction && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {actionType === 'rescueCoti' ? 'Fee Recipient' : 'Rescue Recipient'}
                </Label>
                <div className="text-sm text-muted-foreground">
                  {recipientLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading on-chain recipient…
                    </span>
                  ) : hardcodedRecipient ? (
                    <code className="text-xs bg-muted px-2 py-1 rounded inline-block break-all max-w-full">
                      {hardcodedRecipient}
                    </code>
                  ) : (
                    <span className="text-xs text-destructive">Could not load recipient from contract</span>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {isPod
                      ? 'Configured on the PrivacyPortalFactory — funds always go to this address.'
                      : 'Hardcoded on the contract — funds always go to this address.'}
                  </p>
                </div>
              </div>
            )}

            {needsValue && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="value">Amount (in {rescueAmountSymbol})</Label>
                  <div className="text-xs text-muted-foreground">
                    {balanceLoading ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading balance…
                      </span>
                    ) : availableBalance != null ? (
                      <span>
                        Available:{' '}
                        <span className="text-foreground font-medium">
                          {availableBalance} {rescueAmountSymbol}
                        </span>
                      </span>
                    ) : (
                      <span>Available: —</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    id="value"
                    type="text"
                    placeholder={
                      actionType === 'rescueCoti'
                        ? `e.g., 0.01 ${feeSymbol}`
                        : tokenSymbol
                          ? `e.g., 1 ${tokenSymbol}`
                          : 'Amount to rescue'
                    }
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="glass"
                    disabled={isLoading || success}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 glass"
                    disabled={isLoading || success || balanceLoading || !hasPositiveBalance}
                    onClick={() => {
                      if (availableBalance != null && hasPositiveBalance) {
                        setValue(availableBalance);
                      }
                    }}
                  >
                    Max
                  </Button>
                </div>
                {exceedsAvailable && (
                  <p className="text-xs text-destructive">
                    Amount exceeds available balance ({availableBalance} {rescueAmountSymbol})
                  </p>
                )}
                {!balanceLoading && availableBalance != null && !hasPositiveBalance && (
                  <p className="text-xs text-muted-foreground">
                    Nothing available to rescue.
                  </p>
                )}
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="glass overflow-hidden max-w-full">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <AlertDescription className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="glass border-green-500">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-500">
                  Transaction successful! Closing...
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Right Panel: Fee Simulation (only for fee actions) */}
          {isFeeAction && (
            <div className="py-4 md:border-l md:border-border/50 md:pl-6">
              <SimulatePanel
                fixedFee={fixedFee}
                percentageBps={percentageBps}
                maxFee={maxFee}
                actionType={actionType}
                tokenSymbol={tokenSymbol}
                bridgeAddress={bridgeAddress}
                chainId={chainId}
                isPod={isPod}
                feeSymbol={feeSymbol}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading} className="glass">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || success || rescueAmountInvalid}
            className="bg-primary hover:bg-primary/90"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLoading
              ? 'Processing...'
              : actionType === 'enableDeposits'
                ? 'Enable Deposits'
                : actionType === 'disableDeposits'
                  ? 'Disable Deposits'
                  : 'Execute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminActionModal;
